// Merge a duplicate patient from the record (UC-PATIENTS-021; PAT-65, D10b): pick the duplicate with the typeahead, read it
// and show the two side by side, choose which record stays, give the reason, then POST /patients/{survivor}/merge — a
// Control PIN action (PatientMerge, D61) with one Idempotency-Key per body. Owner and Admin only (patients.patient.merge).
// A refusal stays in the dialog with what was typed; the parent opens the survivor afterwards. The prototype has no merge
// screen; the dialog uses its vocabulary (patient-record.html's kv rows, the lock mark, the warn alert).
import type { MergeResult, PatientView } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { newIdempotencyKey } from "../api/client";
import { patients } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate } from "../lib/format";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, ErrorAlert, Field, Loading, LockMark, Modal, TextArea } from "../ui/bits";
import { CREATED_SOURCE_LABEL, GENDER_SHORT, PatientPicker, PatientStatusChip, patientName, useBranchNames, useLeaveGuard, type PickedPatient } from "./PatientsShared";

const REFUSAL: Record<string, string> = {
  has_dependants: "The record being merged away still has dependants. Detach them or move them to another main member first.",
  already_merged: "One of the two records has already been merged into another patient.",
};

const dash = <span className="faint">—</span>;
const maLine = (p: PatientView): ReactNode => (p.medicalAid ? <>{[p.medicalAid.schemeName, p.medicalAid.optionName ?? p.medicalAid.planName].filter(Boolean).join(" · ") || "Medical aid"}{p.medicalAid.membershipNumber && <> · <span className="mono">{p.medicalAid.membershipNumber}</span></>}</> : "Cash patient");

export function MergeModal({ p, onClose, onDone }: { p: PatientView; onClose: () => void; onDone: (r: MergeResult, survivor: PatientView, merged: PatientView) => void }) {
  const withStepUp = useStepUp();
  const { name: branchName } = useBranchNames();
  const [picked, setPicked] = useState<PickedPatient | null>(null);
  const [keep, setKeep] = useState<"this" | "other">("this");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // From Authorise until the answer — several seconds (UC-PATIENTS-021 step 6): said in the dialog, and leaving the page asks first.
  const [working, setWorking] = useState(false);
  useLeaveGuard(working);
  const [error, setError] = useState<unknown>(null);
  const other = useQuery({ queryKey: ["patient", picked?.id], queryFn: () => patients.get(picked!.id), enabled: !!picked });
  const o = other.data?.data ?? null;
  const survivor = keep === "this" ? p : o;
  const merged = keep === "this" ? o : p;
  // One key per logical request: a changed duplicate, direction or reason is a new body (BR-TENANCY-013).
  const body = survivor && merged ? JSON.stringify([survivor.id, merged.id, reason.trim()]) : "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = useMemo(() => newIdempotencyKey(), [body]);
  useEffect(() => { setError(null); }, [body]);
  // The PIN prompt's Escape reaches this dialog too: while a merge is in flight the dialog stays.
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);

  const submit = async () => {
    if (!survivor || !merged || !reason.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => { if (g.token) setWorking(true); return patients.merge(survivor.id, { mergedPatientId: merged.id as never, reason: reason.trim() }, { ...g, idempotencyKey: key }); }, {
        title: `Merge ${merged.accountCode} into ${survivor.accountCode}`, reason: "none",
        description: `Everything on ${patientName(merged)} (${merged.accountCode}) moves to ${patientName(survivor)} (${survivor.accountCode}); ${merged.accountCode} becomes a searchable alias and the duplicate is voided. This cannot be undone. The reason is written to the Control Log.`,
      });
      if (r) onDone(r.data, survivor, merged);
    } catch (err) { setError(err); }
    finally { setBusy(false); setWorking(false); }
  };

  const refusal = isApiError(error) && typeof error.meta.reason === "string" ? REFUSAL[error.meta.reason] : undefined;
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  const rows: [string, (x: PatientView) => ReactNode][] = [
    ["Account code", (x) => <span className="mono">{x.accountCode}</span>],
    ["Name", (x) => <>{x.title ? `${x.title} ` : ""}{patientName(x)}</>],
    ["Date of birth", (x) => <>{formatDate(x.dateOfBirth)} · {x.age} · {GENDER_SHORT[x.gender]}</>],
    ["ID number", (x) => (x.idNumber ? <span className="mono">{x.idNumber}</span> : dash)],
    ["Mobile", (x) => x.contact.mobile ?? dash],
    ["Email", (x) => x.contact.email ?? dash],
    ["Medical aid", maLine],
    ["Home branch", (x) => branchName(x.homeBranchId) ?? dash],
    ["Family", (x) => (x.mainAccountId ? "Dependant" : "Main member")],
    ["Status", (x) => <PatientStatusChip status={x.status} reason={x.statusReason} />],
    ["Created", (x) => <>{formatDate(x.audit.createdAt)} · {CREATED_SOURCE_LABEL[x.createdSource]}</>],
  ];

  return (
    <Modal title={`Merge a duplicate into ${keep === "this" ? patientName(p) : o ? patientName(o) : "the other record"}`} meta={<>Owner or Admin · not reversible</>} wide onClose={close} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={close} disabled={busy}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy || !survivor || !merged || !reason.trim()}>{busy ? "Merging…" : "Merge"}<LockMark /></button>
    </>}>
      {working && survivor && merged && <Alert tone="info" role="status"><b>Merging {merged.accountCode} into {survivor.accountCode}…</b> This takes several seconds. Keep this page open; {survivor.accountCode} opens when the merge is done.</Alert>}
      {error !== null && !reasonErr && <Alert tone="crit"><b>Not merged.</b> {refusal ?? describeError(error)}</Alert>}
      <PatientPicker label="The duplicate" required value={picked} onPick={(x) => { setPicked(x ? { id: x.id, displayName: x.displayName, accountCode: x.accountCode } : null); setKeep("this"); }} exclude={[p.id]} help="Search by name, account code, ID number or mobile" />
      {picked && other.isPending && <Loading what="the duplicate" />}
      {picked && other.isError && <ErrorAlert error={other.error} />}
      {o && (
        <>
          <div className="field">
            <label>Which record stays</label>
            <div className="stack gap-4">
              <label className="check"><input type="radio" name="merge-keep" checked={keep === "this"} onChange={() => setKeep("this")} /><span className={`box radio${keep === "this" ? " on" : ""}`} /><span>This record — <span className="mono">{p.accountCode}</span> stays; <span className="mono">{o.accountCode}</span> is merged into it</span></label>
              <label className="check"><input type="radio" name="merge-keep" checked={keep === "other"} onChange={() => setKeep("other")} /><span className={`box radio${keep === "other" ? " on" : ""}`} /><span>The other record — <span className="mono">{o.accountCode}</span> stays; <span className="mono">{p.accountCode}</span> is merged into it</span></label>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table aria-label="The two records side by side">
              <thead><tr><th></th><th>Stays</th><th>Merged away</th></tr></thead>
              <tbody>
                {rows.map(([label, cell]) => <tr key={label}><td className="muted">{label}</td><td>{cell(survivor!)}</td><td>{cell(merged!)}</td></tr>)}
              </tbody>
            </table>
          </div>
          <Alert tone="warn"><b>Not reversible.</b> Every document, membership, consent, appointment and account number of <span className="mono">{merged!.accountCode}</span> moves to <span className="mono">{survivor!.accountCode}</span>; <span className="mono">{merged!.accountCode}</span> stays findable as an alias and its record is voided, never deleted.</Alert>
          <Field label="Reason" required htmlFor="merge-reason" error={reasonErr} help="Written to the Control Log with the authoriser"><TextArea id="merge-reason" value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} /></Field>
          <div className="help"><LockMark>requires Control PIN</LockMark> — an Owner or Admin authorises the merge.</div>
        </>
      )}
    </Modal>
  );
}
